/**
 * Timeline API Client (PROJ-007)
 * Incident activity history and audit trail
 */

import { apiRequest } from './standard-api';
import type {
  TimelineEvent,
  ListTimelineEventsParams,
  TimelineStats,
  CreateTimelineEventPayload
} from '@/types/timeline';

/**
 * List timeline events for an incident
 */
export async function listTimelineEvents(
  incidentId: string,
  params?: ListTimelineEventsParams
): Promise<{ data: TimelineEvent[]; meta: any }> {
  const queryParams = new URLSearchParams();

  if (params?.page) queryParams.set('page', params.page.toString());
  if (params?.limit) queryParams.set('limit', params.limit.toString());
  if (params?.kind) queryParams.set('kind', params.kind);
  if (params?.includeActor !== undefined)
    queryParams.set('includeActor', params.includeActor.toString());
  if (params?.startDate) queryParams.set('startDate', params.startDate);
  if (params?.endDate) queryParams.set('endDate', params.endDate);

  const query = queryParams.toString();
  const url = `/api/v1/incidents/${incidentId}/timeline${query ? `?${query}` : ''}`;

  const response = await apiRequest<{ data: TimelineEvent[]; meta: any }>(url);
  return response.data;
}

/**
 * Get a single timeline event
 */
export async function getTimelineEvent(
  incidentId: string,
  eventId: string
): Promise<TimelineEvent> {
  const response = await apiRequest<TimelineEvent>(
    `/api/v1/incidents/${incidentId}/timeline/${eventId}`
  );
  return response.data;
}

/**
 * Create a timeline event (manual comment)
 */
export async function createTimelineEvent(
  incidentId: string,
  payload: CreateTimelineEventPayload
): Promise<TimelineEvent> {
  const response = await apiRequest<TimelineEvent>(
    `/api/v1/incidents/${incidentId}/timeline`,
    {
      method: 'POST',
      body: JSON.stringify(payload)
    }
  );
  return response.data;
}

/**
 * Get timeline statistics
 */
export async function getTimelineStats(incidentId: string): Promise<TimelineStats> {
  const response = await apiRequest<TimelineStats>(
    `/api/v1/incidents/${incidentId}/timeline/stats`
  );
  return response.data;
}

/**
 * Helper: Create a comment event
 */
export async function addComment(
  incidentId: string,
  message: string
): Promise<TimelineEvent> {
  return createTimelineEvent(incidentId, {
    kind: 'comment',
    message
  });
}
